import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import discountCodeApiController from "../../../services/discountCode.service";

const rowsFrom = (response) => {
  const data = response?.data;
  if (Array.isArray(data)) return data;
  return Array.isArray(data?.data) ? data.data : [];
};

const codeId = (record) => Number(record?.id ?? record?.discount_code_id);

const useDiscountCodeRecords = (includeInactive = false) => {
  const [records, setRecords] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;
    discountCodeApiController.view({ include_inactive: includeInactive })
      .then((response) => {
        if (!mounted) return;
        setRecords(rowsFrom(response).filter((record) => includeInactive || record.is_active !== false));
      })
      .catch(() => {
        if (mounted) setError("Discount codes could not be loaded.");
      });
    return () => { mounted = false; };
  }, [includeInactive]);

  return { records, error };
};

export const SubscriptionDiscountCodeList = ({
  discountCodes = [],
  onRemove,
  title = "Discount codes",
  emptyText = "",
}) => {
  const { t } = useTranslation();
  const rows = Array.isArray(discountCodes) ? discountCodes : [];
  if (rows.length === 0 && !emptyText) return null;
  return (
    <div className="mb-3">
      <h6 className="fw-semibold mb-2">{t(title)}</h6>
      {rows.length === 0 ? <div className="text-muted">{t(emptyText)}</div> : null}
      <div className="d-flex flex-wrap gap-2">
        {rows.map((record, index) => (
          <span className="badge bg-light text-dark" key={codeId(record) || index}>
            {record.code ?? record.name ?? `${t("Code")} ${codeId(record)}`}
            {onRemove ? (
              <button
                type="button"
                className="btn btn-link p-0 ms-2"
                aria-label={`${t("Remove")} ${record.code ?? codeId(record)}`}
                onClick={() => onRemove(
                  title === "Discount codes"
                    ? codeId(record)
                    : rows.filter((candidate) => codeId(candidate) !== codeId(record)).map(codeId),
                )}
              >
                <i className="fal fa-times"></i>
              </button>
            ) : null}
          </span>
        ))}
      </div>
    </div>
  );
};

/**
 * SOF-312: the discount-code dropdown on the middle screen. Codes are searched
 * and toggled straight from the middle screen.
 */
export const SubscriptionDiscountCodePicker = ({ records = [], error = "", value = [], onChange, searchTerm }) => {
  const { t } = useTranslation();
  const [localSearch, setLocalSearch] = useState("");
  const search = searchTerm === undefined ? localSearch : searchTerm;
  const term = search.trim().toLowerCase();
  const selected = new Set(value.map(Number));
  const visible = term
    ? records.filter((record) => (
      selected.has(codeId(record))
      || String(record?.code ?? record?.name ?? "").toLowerCase().includes(term)
    ))
    : records;
  const toggle = (id) => {
    onChange(selected.has(id)
      ? value.map(Number).filter((candidate) => candidate !== id)
      : [...value.map(Number), id]);
  };

  return (
    <div className="subscription-discount-codes-picker">
      <input
        type="text"
        className="form-control search-input mb-3"
        placeholder={t("Search discount codes")}
        aria-label={t("Search discount codes")}
        value={search}
        onChange={(event) => setLocalSearch(event.target.value)}
        readOnly={searchTerm !== undefined}
      />
      {error && <div className="text-danger">{error}</div>}
      {records.length === 0 && !error && <div className="text-muted">{t("No active discount codes.")}</div>}
      {records.length > 0 && visible.length === 0 ? <div className="text-muted">{t("No matching discount codes.")}</div> : null}
      {visible.map((record) => {
        const id = codeId(record);
        return (
          <label className="d-block mb-2" key={id}>
            <input
              className="form-check-input me-2"
              type="checkbox"
              checked={selected.has(id)}
              onChange={() => toggle(id)}
            />
            {record.code}
          </label>
        );
      })}
    </div>
  );
};

const SubscriptionDiscountCodesField = ({ value = [], onChange, mode = "full", searchTerm }) => {
  const { t } = useTranslation();
  const { records, error } = useDiscountCodeRecords(mode === "included");
  const linkedRecords = value.map((id) => records.find((record) => codeId(record) === Number(id)) || { id: Number(id) });
  const removeCode = (id) => onChange(value.map(Number).filter((candidate) => candidate !== id));

  if (mode === "picker") {
    return <SubscriptionDiscountCodePicker records={records} error={error} value={value} onChange={onChange} searchTerm={searchTerm} />;
  }
  if (mode === "included") {
    return <SubscriptionDiscountCodeList discountCodes={linkedRecords} onRemove={removeCode} />;
  }
  return (
    <fieldset className="subscription-discount-codes-field mt-4">
      <legend className="h4 fw-semibold mb-2">{t("Discount codes")}</legend>
      <p className="subtitle">{t("Choose codes available to clients with this subscription.")}</p>
      <SubscriptionDiscountCodePicker records={records} error={error} value={value} onChange={onChange} searchTerm={searchTerm} />
      <SubscriptionDiscountCodeList discountCodes={linkedRecords} onRemove={removeCode} />
    </fieldset>
  );
};

SubscriptionDiscountCodesField.propTypes = {
  value: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
  onChange: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(["full", "picker", "included"]),
  searchTerm: PropTypes.string,
};

SubscriptionDiscountCodePicker.propTypes = {
  records: PropTypes.arrayOf(PropTypes.object),
  error: PropTypes.string,
  value: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
  onChange: PropTypes.func.isRequired,
  searchTerm: PropTypes.string,
};

SubscriptionDiscountCodeList.propTypes = {
  discountCodes: PropTypes.arrayOf(PropTypes.object),
  onRemove: PropTypes.func,
  title: PropTypes.node,
  emptyText: PropTypes.node,
};

export default SubscriptionDiscountCodesField;
